import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { getImageUrl } from '../../utils/imageUrl';
import Button from '../../components/common/Button';
import './Favorites.css';

const Favorites = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { favorites, removeFromFavorites, clearFavorites } = useFavorites();
  const { addToCart } = useCart();

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  const handleRemoveFavorite = (productId) => {
    removeFromFavorites(productId);
  };

  const handleClearAll = () => {
    if (window.confirm(t('favorites.confirmClear'))) {
      clearFavorites();
    }
  };

  if (favorites.length === 0) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-empty">
            <div className="favorites-empty-icon">❤️</div>
            <h2>{t('favorites.empty')}</h2>
            <p>{t('favorites.emptyDescription')}</p>
            <Button onClick={() => navigate('/shop')}>
              {t('cart.continueShopping')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <div className="favorites-container">
        <div className="favorites-header">
          <h1>{t('favorites.title')}</h1>
          <p>{t('favorites.subtitle', { count: favorites.length })}</p>
          <button className="clear-all-btn" onClick={handleClearAll}>
            {t('favorites.clearAll')}
          </button>
        </div>

        <div className="favorites-grid">
          {favorites.map((product) => (
            <div key={product.id} className="favorite-item">
              <button
                className="remove-favorite-btn"
                onClick={() => handleRemoveFavorite(product.id)}
                title={t('favorites.remove')}
              >
                ✕
              </button>

              <div 
                className="favorite-item-image"
                onClick={() => navigate(`/product/${product.slug}`)}
              >
                {product.primary_image ? (
                  <img
                    src={getImageUrl(product.primary_image)}
                    alt={product.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="no-image">📦</div>';
                    }}
                  />
                ) : (
                  <div className="no-image">📦</div>
                )}
              </div>

              <div className="favorite-item-details">
                <div 
                  className="favorite-item-info"
                  onClick={() => navigate(`/product/${product.slug}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <p className="favorite-item-category">
                    {product.category_name || t('product.uncategorized')}
                  </p>
                  <h3 className="favorite-item-name">{product.name}</h3>
                  <p className="favorite-item-price">
                    ₾{parseFloat(product.price).toFixed(2)}
                  </p>
                  <p className={`favorite-item-stock ${product.stock === 0 ? 'out-of-stock' : ''}`}>
                    {product.stock > 0 
                      ? `${product.stock} ${t('product.inStock')}` 
                      : t('product.outOfStock')
                    }
                  </p>
                </div>

                <div className="favorite-item-actions">
                  <Button
                    variant="primary"
                    block
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock === 0}
                  >
                    {product.stock > 0 ? t('product.addToCart') : t('product.outOfStock')}
                  </Button>
                  <Button
                    variant="secondary"
                    block
                    onClick={() => navigate(`/product/${product.slug}`)}
                  >
                    {t('product.viewDetails')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Favorites;